import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Package, Clock, Users } from 'lucide-react';
import { Product } from '../types';
import apiService from '../services/api';
import SEO from '../components/SEO';
import { toProductImageUrl } from '../utils/imageUrl';

const ProductDetail: React.FC = () => {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [arsRate, setArsRate] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError('Producto invalido');
        setIsLoading(false);
        return;
      }
      try {
        setError(null);
        const [response, currency] = await Promise.all([
          apiService.getProduct(Number(id)),
          apiService.getCurrencyRates().catch(() => null)
        ]);
        setProduct(response.product);
        if (currency?.rates?.sell) {
          setArsRate(Number(currency.rates.sell));
        }
      } catch {
        setError('No se pudo cargar el producto.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const getUnitsPerPack = (productName?: string) => {
    const match = String(productName || '').match(/x\s*(\d+)\s*unidades?/i);
    const units = match ? Number(match[1]) : 1;
    return Number.isFinite(units) && units > 0 ? units : 1;
  };

  const getPackPriceARS = () => {
    if (!product?.showPrice) return 'Consultar precio por cantidad';
    const usd = Number(product?.priceUSD || 0);
    if (!usd || !arsRate) return 'Consultar precio por cantidad';
    const units = getUnitsPerPack(product?.name);
    const ars = usd * units * arsRate;
    return `$${Math.round(ars).toLocaleString('es-AR')} ARS por pack`;
  };

  if (isLoading) return <div className="max-w-5xl mx-auto px-4 py-10">Cargando producto...</div>;
  if (error || !product) return <div className="max-w-5xl mx-auto px-4 py-10 text-red-600">{error || 'Producto no encontrado'}</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEO title={product.name} description={product.description || 'Detalle de producto'} />
      <div className="max-w-5xl mx-auto px-4">
        <Link to="/productos" className="inline-flex items-center text-primary-700 hover:text-primary-800 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a productos
        </Link>

        <div className="bg-white rounded-lg shadow p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-96 bg-white border rounded flex items-center justify-center overflow-hidden">
            {product.image ? (
              <img src={toProductImageUrl(product.image)} alt={product.name} className="w-full h-full object-contain" />
            ) : (
              <Package className="h-20 w-20 text-gray-400" />
            )}
          </div>

          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
            <p className="text-gray-600">{product.description || 'Bolsas de plastico LDPE de alta calidad'}</p>

            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-center"><Package className="h-4 w-4 mr-2" />Material: {product.material}</div>
              <div>Grosor: {product.thickness} micrones</div>
              <div>Medida: {Math.round(Number(product.width) * 100)} cm x {Math.round(Number(product.length) * 100)} cm</div>
              <div className="flex items-center"><Clock className="h-4 w-4 mr-2" />Entrega: {Number(product.deliveryDays) === 0 ? 'Inmediata' : `${product.deliveryDays} dias`}</div>
              <div className="flex items-center"><Users className="h-4 w-4 mr-2" />Minimo: {product.minQuantity} unidades</div>
            </div>

            <div className="pt-2">
              <p className="text-lg font-semibold text-gray-700">{getPackPriceARS()}</p>
            </div>

            <Link to={`/contacto?subject=cotizacion&product=${encodeURIComponent(product.name)}`} className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              Solicitar cotizacion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
